import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Edit, Trash2, Power, PowerOff } from 'lucide-react';
import { adminService } from '../../services/api';
import { Medicament } from '../../types';
import MedicamentForm from './MedicamentForm';

interface MedicamentManagementProps {
  isVisible: boolean;
}

const MedicamentManagement: React.FC<MedicamentManagementProps> = ({ isVisible }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingMedicament, setEditingMedicament] = useState<Medicament | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const queryClient = useQueryClient();

  const { data: medicamentsResponse, isLoading } = useQuery({
    queryKey: ['admin-medicaments'],
    queryFn: () => adminService.getMedicaments().then(res => res.data)
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => adminService.deleteMedicament(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-medicaments'] });
    }
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (id: number) => adminService.toggleMedicamentStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-medicaments'] });
    }
  });

  const medicaments = medicamentsResponse?.results || [];
  const filteredMedicaments = medicaments.filter(medicament =>
    medicament.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    medicament.forme.toLowerCase().includes(searchTerm.toLowerCase()) ||
    medicament.dosage_standard.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (medicament: Medicament) => {
    setEditingMedicament(medicament);
    setShowForm(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce médicament ?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleToggleStatus = (id: number) => {
    toggleStatusMutation.mutate(id);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingMedicament(null);
  };

  if (!isVisible) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Gestion des Médicaments</h1>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nouveau Médicament
        </button>
      </div>

      {/* Barre de recherche */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un médicament..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-gray-900 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          />
        </div>
      </div>

      {/* Tableau des médicaments */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Médicament
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Forme
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Dosage
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Indication
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Statut
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredMedicaments.map((medicament) => (
              <tr key={medicament.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{medicament.nom}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{medicament.forme}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{medicament.dosage_standard}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 max-w-xs truncate">
                    {medicament.indication || 'Non renseigné'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    medicament.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {medicament.is_active ? 'Actif' : 'Inactif'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(medicament)}
                      className="text-blue-600 hover:text-blue-900"
                      title="Modifier"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleToggleStatus(medicament.id)}
                      className={`${
                        medicament.is_active ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'
                      }`}
                      title={medicament.is_active ? 'Désactiver' : 'Activer'}
                    >
                      {medicament.is_active ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => handleDelete(medicament.id)}
                      className="text-red-600 hover:text-red-900"
                      title="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredMedicaments.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            {searchTerm ? 'Aucun médicament trouvé pour cette recherche.' : 'Aucun médicament enregistré.'}
          </div>
        )}
      </div>

      {/* Formulaire de médicament */}
      {showForm && (
        <MedicamentForm
          medicament={editingMedicament}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
};

export default MedicamentManagement;